import CURRENCIES from '@/dataset/currencies.json'

export function convertToBytes(input) {
  const units = {
    kb: 1000,
    mb: 1000 ** 2,
    gb: 1000 ** 3,
    tb: 1000 ** 4,
  }

  const result = input
    .trim()
    .toLowerCase()
    .match(/^(\d+)\s*([a-z]+)$/)
  if (!result) {
    throw new Error('Invalid format')
  }

  const value = parseInt(result[1], 10) // the numeric part
  const unit = result[2] // the unit part

  if (units[unit]) {
    return value * units[unit]
  } else {
    throw new Error('Unsupported unit')
  }
}

export function formatBytes(bytes, decimals = 1) {
  if (bytes === 0) return '0 B'

  const k = 1000
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

export function nullableInt(input, fallback = null) {
  const n = parseInt(input)
  return Number.isFinite(n) ? n : fallback
}

export function extractNumber(input) {
  // Remove any character that is not a digit or a period
  const cleanedString = input.replace(/[^0-9.]/g, '')
  // Parse the cleaned string into a floating-point number
  return parseFloat(cleanedString)
}

/**
 * Formats a number as a currency string.
 * @param {number} input - The number to format.
 * @param {string | null} currency - The currency code.
 * @param {boolean} useExponent - Whether to use the exponent.
 * @returns {string} The formatted currency string.
 */
export function currencyFormat(input, currency = null, useExponent = false) {
  let n = Number(input)
  if (useExponent) {
    const { exponent } = CURRENCIES[currency]
    n = n / 10 ** exponent
  }
  const decimal = Number.isInteger(n) ? 0 : 2
  let config = { minimumFractionDigits: decimal, maximumFractionDigits: decimal }
  if (currency) {
    config = { ...config, currency, style: 'currency', currencyDisplay: 'code' }
  }
  let formatter = new Intl.NumberFormat('en-US', config)
  return formatter.format(n)
}

export function findIndexOfMax(arr) {
  if (arr.length === 0) return -1

  let maxIndex = 0
  for (let i = 1; i < arr.length; i++) {
    if (arr[i] > arr[maxIndex]) {
      maxIndex = i
    }
  }

  return maxIndex
}

export function divide(a, b, fallback = null) {
  if (b === 0) return fallback
  if (isNaN(a) || isNaN(b)) return fallback
  return a / b
}

export function randomIntBetween(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export function signedIncrement(num) {
  const sign = Math.sign(num)
  return sign * (Math.abs(num) + 1)
}

/**
 * Clamps a number within a specified range.
 * If number is smaller than min, returns min.
 * If number is larger than max, returns max.
 * Otherwise, returns the number itself.
 *
 * @param {number} number - The number to clamp
 * @param {[number, number]} range - An array containing [min, max] values
 * @return {number} The clamped value
 */
export function clamp(number, range) {
  const [min, max] = range
  return Math.min(Math.max(number, min), max)
}

/**
 * Returns the value from an array that is closest to the target number.
 *
 * @param {number} target - The reference number
 * @param {number[]} values - Array of numbers to compare against
 * @return {number} The closest value from the array
 */
export function findClosest(target, values) {
  if (!values.length) throw new Error('Values array cannot be empty')

  return values.reduce((closest, current) => {
    return Math.abs(current - target) < Math.abs(closest - target) ? current : closest
  }, values[0])
}

/**
 * Moves a number towards a target number by a given step
 * @param {number} current - The starting number
 * @param {number} target - The target number to move towards
 * @param {number} step - The maximum amount to move in one step (always positive)
 * @returns {number} The new value after moving towards the target
 */
export function moveNumberTowards(current, target, step) {
  // If step is not positive, return the current value
  if (step <= 0) return current

  // If current equals target, no movement needed
  if (current === target) return current

  // Determine direction (positive or negative)
  const direction = current < target ? 1 : -1

  // Calculate the actual step (magnitude)
  const actualStep = Math.min(Math.abs(target - current), step)

  // Apply the step in the correct direction
  return current + direction * actualStep
}

/**
 * Formats a fixed-decimal amount with thousands separators (e.g. 1,234.56).
 * @param {number} amount - The amount in major currency units.
 * @param {number} fractionDigits - Decimal places to show.
 * @returns {string} The formatted amount string.
 */
export function formatDecimalAmount(amount, fractionDigits = 2) {
  if (!Number.isFinite(amount)) return ''

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    useGrouping: true,
  }).format(amount)
}

/**
 * Formats a number with commas for thousands separators
 * @param {number} num - The number to format
 * @returns {string} The formatted number string
 */
export function formatNumber(num) {
  if (num == null) return ''
  return new Intl.NumberFormat('en-US').format(num)
}

export function compareWithinSigma(a, b, sigma, method = 'absolute') {
  switch (method) {
    case 'absolute':
      // Compare within absolute tolerance
      return Math.abs(a - b) <= sigma

    case 'relative':
      // Compare within relative tolerance (percentage)
      const larger = Math.max(Math.abs(a), Math.abs(b))
      return larger === 0 ? a === b : Math.abs(a - b) / larger <= sigma

    default:
      throw new Error('Method must be "absolute" or "relative"')
  }
}
